// Right-pane chat surface. Mirrors Reputifly AI Local App.tsx:3569-3715.
// Topbar layout (left → right):
//   [hamburger]  [logo + "Reputifly Agent" wordmark]  /  [company crumb]
//                                                                  [… overflow]
// The … overflow holds: Stop session (when running), Guide, Sign out.
// Sessions auto-spawn on first message — no Start/Stop pill in the topbar.

import { useLayoutEffect } from "react";
import { Menu } from "react-feather";
import { useSession } from "../store/session";
import MessageList from "./MessageList";
import Composer from "./Composer";
import SessionStatusPill from "./SessionStatusPill";
import HelpersChip from "./HelpersChip";
import HelpersSheet from "./HelpersSheet";
import CostReceipt from "./CostReceipt";
import MobileAgentStatus from "./MobileAgentStatus";
import { useIsPhone } from "../lib/usePhoneLayout";

export default function ChatPane() {
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  const companies = useSession((s) => s.companies);
  const messagesByCompany = useSession((s) => s.messagesByCompany);
  const sidebarOpen = useSession((s) => s.sidebarOpen);
  const setSidebarOpen = useSession((s) => s.setSidebarOpen);
  const openAddCompany = useSession((s) => s.openAddCompany);
  const streamingMessageId = useSession((s) => s.streamingMessageId);
  const isPhone = useIsPhone();
  // Phones: the sidebar is hidden, so ☰ carries a dot when another chat has a
  // reply you haven't opened (the same server-side unread the sidebar shows).
  const unreadElsewhere = useSession((s) =>
    Object.entries(s.unreadByCompany).some(([id, at]) =>
      !!at && id !== s.activeCompanyId && s.companies.some((c) => c.id === id)));

  const company = activeCompanyId
    ? companies.find((c) => c.id === activeCompanyId)
    : null;
  const messages = activeCompanyId
    ? (messagesByCompany[activeCompanyId] ?? [])
    : [];

  // Drive body[data-state="empty" | "conversation"] for AI Local CSS.
  // Empty state = no messages to display, regardless of session lifecycle.
  // (Previously we required sessionStatus !== "running" too, which broke
  // centering after Reset / on fresh reload of a warm session — the
  // title would render but the empty-state CSS wouldn't fire because
  // the box still had a claude child alive with zero messages.)
  useLayoutEffect(() => {
    const isEmpty = messages.length === 0;
    document.body.dataset.state = isEmpty ? "empty" : "conversation";
    return () => {
      document.body.dataset.state = "";
    };
  }, [messages.length]);

  return (
    <main className="chat" data-has-company={!!company}>
      <header className="topbar">
        {!sidebarOpen && (
          <button
            type="button"
            className="topbar__sidebarToggle"
            onClick={() => setSidebarOpen(true)}
            aria-label={isPhone && unreadElsewhere ? "Open companies, new replies" : "Open companies"}
          >
            <Menu width={18} height={18} />
            {isPhone && unreadElsewhere && (
              <span className="topbar__unreadDot" aria-hidden="true" />
            )}
          </button>
        )}
        {/* Phones: one glass status capsule beside ☰ replaces the pill + chip. */}
        {isPhone && company && !sidebarOpen && <MobileAgentStatus />}

        <div className="topbar__brand">
          <img
            src="/reputifly-logo.png"
            alt="Reputifly"
            className="topbar__logo"
            draggable={false}
          />
          {company && (
            <>
              <span className="topbar__divider" aria-hidden="true">/</span>
              <span className="topbar__crumb" title={company.apiBase}>
                {company.name}
              </span>
              <SessionStatusPill />
              <HelpersChip />
            </>
          )}
        </div>

        {/* Right-side actions slot — the overflow menu (rendered by
            DashboardShell via portal-style absolute positioning inside
            .topbar__actions) sits here. Keeping this slot empty in
            ChatPane keeps the overflow on top of the topbar layer
            cleanly without overlap. */}
        <div className="topbar__actions" />
      </header>

      {company ? (
        <>
          <MessageList
            key={company.id}
            messages={messages}
            followToken={streamingMessageId}
          />
          <Composer />
          <HelpersSheet />
          <CostReceipt />
        </>
      ) : (
        <div className="chat__welcome">
          <div className="chat__welcomeInner">
            <h1 className="chat__centerTitle">
              Pick a <span className="text-gradient">company</span> to begin
            </h1>
            <button
              type="button"
              className="btn--ghost chat__welcomeCta"
              onClick={openAddCompany}
            >
              + Add a company
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
